import { Button, Empty } from 'antd';
import {
    CSSProperties, Dispatch, memo, ReactNode, useCallback, useEffect, useRef, useState,
} from 'react';
import GoogleMapView from './GoogleMapView';
import { useMapEnginePersistence } from './hooks/useMapEnginePersistence';
import MapboxView from './MapboxView';
import MapLoader from './MapLoader';
import { HybridMapApiKeys, LatLng, MapEngine } from './types';

const DEFAULT_FALLBACK_MS = 5000;
const DEFAULT_PERSIST_TTL_MS = 60 * 60 * 1000; // 1h

interface HybridMapProps {
    apiKeys: HybridMapApiKeys;
    center: LatLng;
    zoom?: number;
    bounds?: [number, number, number, number] | null;
    children?: ReactNode | ReactNode[];
    setMapsLoaded?: Dispatch<any>;
    onCenterChanged?: (center: LatLng) => void;
    useGoogle?: boolean;
    fallbackTimeoutMs?: number;
    setMapEngine?: Dispatch<MapEngine>;
    persistKey?: string;
    persistTtlMs?: number;
    onMapError?: (engine: MapEngine, reason: string) => void;
}

const fallbackContainerStyle: CSSProperties = {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
};

const overlayStyle: CSSProperties = {
    position: 'absolute',
    inset: 0,
    zIndex: 10,
};

const HybridMap = ({
    apiKeys,
    center,
    zoom,
    bounds,
    children,
    setMapsLoaded,
    onCenterChanged,
    useGoogle,
    fallbackTimeoutMs = DEFAULT_FALLBACK_MS,
    setMapEngine,
    persistKey,
    persistTtlMs = DEFAULT_PERSIST_TTL_MS,
    onMapError,
}: HybridMapProps) => {
    const persistence = useMapEnginePersistence({ persistKey, persistTtlMs });

    const [engine, setEngine] = useState<MapEngine>(() => {
        const persisted = persistence.read();

        if (persisted) return persisted;

        return useGoogle ? 'google' : 'mapbox';
    });

    const [googleFailed, setGoogleFailed] = useState(false);
    const [mapboxLoaded, setMapboxLoaded] = useState(false);
    const timerRef = useRef<number | null>(null);
    const isFirstRenderRef = useRef(true);

    const clearTimer = () => {
        if (timerRef.current) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const switchEngine = useCallback((next: MapEngine, reason?: string) => {
        setEngine((prev) => {
            if (prev === next) return prev;

            persistence.write(next);
            if (setMapEngine) setMapEngine(next);
            if (reason && onMapError && prev === 'mapbox') {
                onMapError(prev, reason);
            }

            return next;
        });
    }, [persistence, setMapEngine, onMapError]);

    useEffect(() => {
        if (isFirstRenderRef.current) {
            isFirstRenderRef.current = false;
            if (setMapEngine) setMapEngine(engine);

            return;
        }

        if (useGoogle === undefined) return;

        const expected: MapEngine = useGoogle ? 'google' : 'mapbox';

        if (expected !== engine) switchEngine(expected);
    }, [useGoogle]);

    useEffect(() => {
        if (engine !== 'mapbox') return undefined;

        timerRef.current = window.setTimeout(() => {
            switchEngine('google', 'mapbox-timeout');
        }, fallbackTimeoutMs);

        return () => clearTimer();
    }, [engine, fallbackTimeoutMs, switchEngine]);

    useEffect(() => {
        if (engine === 'mapbox') setMapboxLoaded(false);
    }, [engine]);

    const handleMapboxLoaded = useCallback((map: any) => {
        clearTimer();
        setMapboxLoaded(true);
        if (setMapsLoaded) setMapsLoaded(map);
    }, [setMapsLoaded]);

    const handleGoogleLoaded = useCallback((map: any) => {
        if (setMapsLoaded) setMapsLoaded(map);
    }, [setMapsLoaded]);

    const handleMapboxFatal = useCallback((reason: string) => {
        clearTimer();
        switchEngine('google', reason);
    }, [switchEngine]);

    const handleGoogleLoadError = useCallback(() => {
        if (onMapError) onMapError('google', 'google-load-error');
        setGoogleFailed(true);
    }, [onMapError]);

    const handleRetry = useCallback(() => {
        persistence.clear();
        setGoogleFailed(false);
        switchEngine('mapbox');
    }, [persistence, switchEngine]);

    if (engine === 'google' && googleFailed) {
        return (
            <div style={fallbackContainerStyle}>
                <Empty description="Mapa no disponible" />
                <Button type="primary" onClick={handleRetry}>Reintentar</Button>
            </div>
        );
    }

    if (engine === 'mapbox') {
        return (
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                <MapboxView
                    apiKey={apiKeys.mapbox}
                    center={center}
                    zoom={zoom}
                    bounds={bounds}
                    onLoaded={handleMapboxLoaded}
                    onCenterChanged={onCenterChanged}
                    onFatalError={handleMapboxFatal}
                >
                    {children}
                </MapboxView>
                {!mapboxLoaded && <MapLoader style={overlayStyle} />}
            </div>
        );
    }

    return (
        <GoogleMapView
            apiKey={apiKeys.google}
            center={center}
            zoom={zoom}
            bounds={bounds}
            onLoaded={handleGoogleLoaded}
            onCenterChanged={onCenterChanged}
            onLoadError={handleGoogleLoadError}
        >
            {children}
        </GoogleMapView>
    );
};

export default memo(HybridMap);
